import { Button, Chip, List, ListItem, ListItemText, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "react-toastify";
import ConfirmDialog from "components/Modals/ConfirmDialog";
import { reissueDocumentTemplateSignedCopies } from "utils/documentService";

const serverErrorMessage = (error, fallback) =>
  error?.response?.data?.errorMessage || error?.response?.data?.message || fallback;

const OUTCOME_CHIP = {
  READY: { label: "Will reissue", color: "success" },
  REISSUED: { label: "Reissued", color: "success" },
  SKIPPED: { label: "Left as is", color: "warning" },
};

const ReportList = ({ items }) => (
  <List dense disablePadding>
    {items.map((item) => (
      <ListItem
        key={item.packetId}
        disableGutters
        secondaryAction={<Chip size="small" {...OUTCOME_CHIP[item.outcome]} />}
      >
        <ListItemText primary={item.clientName} secondary={item.detail} sx={{ pr: 12 }} />
      </ListItem>
    ))}
  </List>
);

/**
 * Rebuilds signed copies made before Preserve was on so they carry the original's encryption.
 * Always previews with a dry run first; the copies being replaced are kept, never deleted.
 */
const ReissueSignedCopiesButton = ({ template }) => {
  const queryClient = useQueryClient();
  const [preview, setPreview] = useState(null);

  const previewMutation = useMutation({
    mutationFn: () =>
      reissueDocumentTemplateSignedCopies({ templateId: template.id, dryRun: true }),
    onSuccess: setPreview,
    onError: (error) => toast.error(serverErrorMessage(error, "Could not check the signed copies")),
  });
  const reissueMutation = useMutation({
    mutationFn: () =>
      reissueDocumentTemplateSignedCopies({ templateId: template.id, dryRun: false }),
    onSuccess: (report) => {
      setPreview(null);
      queryClient.invalidateQueries({ queryKey: ["document-templates"] });
      queryClient.invalidateQueries({ queryKey: ["document-packets"] });
      queryClient.invalidateQueries({ queryKey: ["visaDocumentStatuses"] });
      toast.success(
        `Reissued ${report.reissued} signed ${report.reissued === 1 ? "copy" : "copies"}`
      );
      if (report.skipped > 0) {
        toast.warning(`${report.skipped} left as they were. Check them before trying again.`);
      }
    },
    onError: (error) =>
      toast.error(serverErrorMessage(error, "Could not reissue the signed copies")),
  });

  const count = template.signedCopiesToReissue || 0;
  if (count === 0) {
    return null;
  }

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        sx={{ mt: 1 }}
        disabled={previewMutation.isLoading}
        onClick={(event) => {
          event.stopPropagation();
          previewMutation.mutate();
        }}
      >
        {`Reissue ${count} signed ${count === 1 ? "copy" : "copies"}`}
      </Button>
      <ConfirmDialog
        open={Boolean(preview)}
        title="Reissue signed copies"
        message="Each copy is rebuilt on the original form from the answers and signatures stored when it was signed, so it keeps the original's encryption. The copy it replaces is kept and stays downloadable. Clients are not emailed."
        content={
          preview && (
            <>
              <ReportList items={preview.items} />
              {preview.skipped > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Copies left as is keep their current file untouched.
                </Typography>
              )}
            </>
          )
        }
        confirmLabel={`Reissue ${preview?.ready ?? 0}`}
        loading={reissueMutation.isLoading}
        onConfirm={() => (preview?.ready ? reissueMutation.mutate() : setPreview(null))}
        onCancel={() => setPreview(null)}
      />
    </>
  );
};

export default ReissueSignedCopiesButton;
